/**
 * EoT Claim Builder — Contracts & Claims.
 *
 * All eight tabs ship in full: Setup, Time Rules, Events, Schedule Health,
 * Analysis, Entitlement, Claim Pack, Review & Tracking — plus a reading tab,
 * "How this works", explaining the process end to end.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Gavel, Plus, Loader2, Upload, Link2, FileText, Check, X, Pencil, Lock, Unlock,
  CalendarClock, ArrowLeft, Sparkles, Trash2, Info, ShieldCheck,
} from 'lucide-react';
import ScheduleHealthTab from './eot/ScheduleHealthTab';
import DelayAnalysisTab from './eot/DelayAnalysisTab';
import EntitlementTab from './eot/EntitlementTab';
import ClaimPackTab from './eot/ClaimPackTab';
import ReviewTrackingTab from './eot/ReviewTrackingTab';
import EoTGuideTab from './eot/EoTGuideTab';

const TONE = {
  COMPLIANT: { bg: '#DCFCE7', color: '#166534' },
  LATE: { bg: '#FEF3C7', color: '#92400E' },
  MISSING: { bg: '#FEE2E2', color: '#991B1B' },
  UNKNOWN: { bg: '#E2E8F0', color: '#334155' },
  EMPLOYER: { bg: '#DBEAFE', color: '#1E40AF' },
  CONTRACTOR: { bg: '#FEE2E2', color: '#991B1B' },
  NEUTRAL: { bg: '#E2E8F0', color: '#334155' },
  ACCEPTED: { bg: '#DCFCE7', color: '#166534' },
  EDITED: { bg: '#DBEAFE', color: '#1E40AF' },
  REJECTED: { bg: '#FEE2E2', color: '#991B1B' },
  SUGGESTED: { bg: '#FEF3C7', color: '#92400E' },
  CONFIRMED: { bg: '#DCFCE7', color: '#166534' },
  CANDIDATE: { bg: '#FEF3C7', color: '#92400E' },
};
const chip = (k) => TONE[k] || TONE.UNKNOWN;

const Pill = ({ value, testId }) => value ? (
  <span style={{ background: chip(String(value).split(' ')[0]).bg, color: chip(String(value).split(' ')[0]).color }}
        className="inline-block px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider whitespace-normal text-left leading-snug"
        data-testid={testId}>{value}</span>
) : <span className="text-muted-foreground text-[10px]">—</span>;

const mmss = (s) => {
  if (s == null) return null;
  if (s < 60) return `${Math.max(s, 1)} sec`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(Math.round(s % 60)).padStart(2, '0')}s`;
  return `${Math.floor(s / 3600)} hr ${Math.round((s % 3600) / 60)} min`;
};

// A long read is honest about itself: which pass it is on, how far through, and
// how long it still needs. The clock ticks here between server updates, so the
// bar moves within a pass rather than jumping at the boundaries.
const RunProgress = ({ progress, what, testid }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const started = progress?.startedAt ? Date.parse(progress.startedAt) : null;
  const updated = progress?.updatedAt ? Date.parse(progress.updatedAt) : started;
  const sinceUpdate = updated ? Math.max(0, (now - updated) / 1000) : 0;
  const elapsed = started ? Math.max(0, Math.round((now - started) / 1000)) : null;
  const done = progress?.done || 0;
  const total = progress?.total || 0;
  // Within the current pass, lean on how long the finished passes took.
  const perPass = done > 0 && progress?.elapsedSeconds ? progress.elapsedSeconds / done : 75;
  const fraction = Math.min(0.95, sinceUpdate / perPass);
  const pct = total
    ? Math.min(99, Math.round((100 * (done + fraction)) / total))
    : Math.min(8, Math.round(sinceUpdate));
  const serverEta = progress?.etaSeconds;
  const eta = serverEta != null
    ? Math.max(0, Math.round(serverEta - sinceUpdate))
    : (total ? Math.max(0, Math.round((total - done - fraction) * perPass)) : null);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-foreground">{progress?.phase || `Cognara is reading ${what}`}</span>
        <span className="text-muted-foreground tabular-nums" data-testid={`${testid}-pct`}>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-foreground/70 transition-[width] duration-1000 ease-linear"
             style={{ width: `${Math.max(pct, 3)}%` }} />
      </div>
      <p className="text-[10px] text-muted-foreground" data-testid={`${testid}-eta`}>
        {elapsed != null ? `${mmss(elapsed)} elapsed` : 'starting'}
        {eta ? ` · about ${mmss(eta)} left` : ''}
        {total ? ` · ${total} reading pass${total > 1 ? 'es' : ''}` : ''}
      </p>
    </div>
  );
};


const Picker = ({ label, hint, options, value, multi, onChange, testId }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map(o => {
        const on = multi ? (value || []).includes(o.key) : value === o.key;
        return (
          <button key={o.key} type="button" title={o.tip || ''}
                  data-testid={`${testId}-${o.key}`}
                  onClick={() => onChange(multi
                    ? (on ? value.filter(v => v !== o.key) : [...(value || []), o.key])
                    : o.key)}
                  className={`px-2.5 py-1.5 rounded-md border text-[11px] text-left transition-colors ${on
                    ? 'border-[#6B5CE7] bg-[#6B5CE7]/10 text-foreground'
                    : 'border-border text-muted-foreground hover:border-[#6B5CE7]/50'}`}>
            {o.label}{o.tip && <Info className="w-3 h-3 inline ml-1 opacity-50" />}
          </button>
        );
      })}
    </div>
    {hint && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const Field = ({ label, children }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{label}</p>{children}</div>
);

export default function EoTClaimBuilder({ projectId, contractId, contractLabel, sourceClaim, startNew, onBack }) {
  const [options, setOptions] = useState(null);
  const [sources, setSources] = useState({ documents: [], schedules: [] });
  const [claims, setClaims] = useState([]);
  const [open, setOpen] = useState(null);          // the claim being worked on
  const [tab, setTab] = useState('setup');
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({
    title: '', claimType: 'INTERIM', claimant: 'CONTRACTOR', periodFrom: '', periodTo: '',
    contractStartDate: '', originalCompletionDate: '', eotAlreadyGrantedDays: 0,
    methods: ['TIA'], concurrencyRule: 'MALMAISON', prolongationMethod: 'ACTUAL',
    analysisSource: 'COGNARA_AI', documents: [], schedules: [],
  });
  const [rules, setRules] = useState(null);
  const [events, setEvents] = useState(null);
  const [editRule, setEditRule] = useState(null);
  const fileRef = useRef(null);

  const loadClaims = useCallback(async () => {
    if (!projectId) return;
    try { setClaims((await api.get(`/eot/claims?projectId=${projectId}`)).data.claims || []); }
    catch { /* a project with no claims yet */ }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const [o, s] = await Promise.all([api.get('/eot/options'), api.get(`/eot/sources?projectId=${projectId}`)]);
        setOptions(o.data); setSources(s.data);
      } catch { /* ignore */ }
      loadClaims();
    })();
  }, [projectId, loadClaims]);

  const refreshOpen = useCallback(async (id) => {
    const [c, r, e] = await Promise.all([
      api.get(`/eot/claims/${id}`), api.get(`/eot/claims/${id}/time-rules`), api.get(`/eot/claims/${id}/events`)]);
    // The claim holds its picks under `sources`; the setup form edits them flat.
    setOpen({ ...c.data, documents: c.data.sources?.documents || [], schedules: c.data.sources?.schedules || [] });
    setRules(r.data); setEvents(e.data);
    return { rules: r.data, events: e.data };
  }, []);

  // Opened from a claim in the register: find or raise its EoT workspace and go
  // straight into it — the register stays the only list of claims. The guard keeps
  // a double mount (StrictMode) from asking for the same workspace twice.
  const openedFor = useRef('');
  useEffect(() => {
    if (!sourceClaim?.id || openedFor.current === sourceClaim.id) return;
    openedFor.current = sourceClaim.id;
    (async () => {
      try {
        const { data } = await api.post('/eot/claims/for-claim', {
          claimId: sourceClaim.id, projectId, contractId, contractLabel });
        await refreshOpen(data.id);
      } catch (e) {
        openedFor.current = '';
        toast.error(e.response?.data?.detail || 'Could not open the EoT workspace');
      }
    })();
  }, [sourceClaim?.id, projectId, contractId, contractLabel, refreshOpen]);

  // While either AI pass is running, keep asking until it finishes.
  useEffect(() => {
    if (!open?.id) return undefined;
    const running = rules?.status === 'RUNNING' || events?.status === 'RUNNING' || open?.rescanStatus === 'RUNNING';
    if (!running) return undefined;
    const t = setInterval(() => refreshOpen(open.id).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [open?.id, rules?.status, events?.status, open?.rescanStatus, refreshOpen]);

  const create = async () => {
    setBusy('create');
    try {
      const { data } = await api.post('/eot/claims', { ...form, projectId, contractId, contractLabel });
      if (form.documents.length) await api.post(`/eot/claims/${data.id}/evidence/link`, { documents: form.documents });
      toast.success('EoT claim created');
      setShowNew(false); setForm(f => ({ ...f, title: '', documents: [], schedules: [] }));
      await loadClaims(); await refreshOpen(data.id); setTab('setup');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create the claim'); }
    finally { setBusy(''); }
  };

  const createInRegister = async () => {
    setBusy('create');
    try {
      const { data } = await api.post('/eot/claims/new-in-register', {
        contractId, projectId, contractLabel, title: form.title, periodFrom: form.periodFrom });
      await api.patch(`/eot/claims/${data.id}`, {
        title: form.title, claimType: form.claimType, claimant: form.claimant,
        periodFrom: form.periodFrom, periodTo: form.periodTo,
        contractStartDate: form.contractStartDate, originalCompletionDate: form.originalCompletionDate,
        eotAlreadyGrantedDays: form.eotAlreadyGrantedDays, methods: form.methods,
        concurrencyRule: form.concurrencyRule, prolongationMethod: form.prolongationMethod,
        analysisSource: form.analysisSource,
        sources: { documents: form.documents || [], schedules: form.schedules || [] },
      });
      if ((form.documents || []).length) {
        await api.post(`/eot/claims/${data.id}/evidence/link`, { documents: form.documents });
      }
      toast.success(`EoT claim ${data.claimNumber || ''} raised in the register`.trim());
      await refreshOpen(data.id);
      setTab('setup');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not raise the claim'); }
    finally { setBusy(''); }
  };

  const rescan = async () => {
    setBusy('rescan');
    try {
      await api.post(`/eot/claims/${open.id}/evidence/rescan`, { force: true });
      toast.success('Cognara is reading the attached documents again');
      await refreshOpen(open.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start the re-read'); }
    finally { setBusy(''); }
  };

  const datesTried = useRef('');
  const readDates = useCallback(async (overwrite, quiet) => {
    if (!quiet) setBusy('dates');
    try {
      const { data } = await api.post(`/eot/claims/${open.id}/dates/suggest`, { overwrite });
      const applied = Object.keys(data.applied || {}).length;
      if (!quiet) {
        toast[applied ? 'success' : 'info'](applied
          ? `${applied} date(s) read from ${(data.documentsRead || []).length} document(s)`
          : 'The documents do not state those dates — enter them here');
      }
      await refreshOpen(open.id);
    } catch (e) { if (!quiet) toast.error(e.response?.data?.detail || 'Could not read the dates'); }
    finally { if (!quiet) setBusy(''); }
  }, [open?.id, refreshOpen]);

  // Dates come off the papers the first time a claim has something readable
  // attached — the user should not type what the contract already says. Tried
  // once per claim, guarded so a refresh mid-flight cannot ask again.
  useEffect(() => {
    if (!open?.id || open.datesSuggestedAt || datesTried.current === open.id) return;
    if (open.contractStartDate && open.originalCompletionDate) return;
    if (!(open.evidence || []).some(e => e.textChars)) return;
    datesTried.current = open.id;
    readDates(false, true);
  }, [open?.id, open?.datesSuggestedAt, open?.contractStartDate, open?.originalCompletionDate,
      open?.evidence, readDates]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy('upload');
    try {
      const fd = new FormData();
      [...files].forEach(f => fd.append('files', f));
      const { data } = await api.post(`/eot/claims/${open.id}/evidence`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${data.uploaded.length} document(s) added and read`);
      await refreshOpen(open.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(''); if (fileRef.current) fileRef.current.value = ''; }
  };

  const linkDocs = async (picked) => {
    if (!picked?.length) return;
    setBusy('link');
    try {
      const { data } = await api.post(`/eot/claims/${open.id}/evidence/link`, { documents: picked });
      const added = (data.linked || []).length;
      if (added === 0) toast.info('Those documents are already in this claim');
      else toast.success(`${added} document(s) pulled in — ${data.readable || 0} carried readable text`);
      await refreshOpen(open.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not link'); }
    finally { setBusy(''); }
  };

  const saveSetup = async () => {
    setBusy('save');
    try {
      const picks = open.documents || [];
      const payload = {
        title: open.title, claimType: open.claimType, claimant: open.claimant,
        periodFrom: open.periodFrom, periodTo: open.periodTo,
        contractStartDate: open.contractStartDate, originalCompletionDate: open.originalCompletionDate,
        eotAlreadyGrantedDays: open.eotAlreadyGrantedDays, methods: open.methods,
        concurrencyRule: open.concurrencyRule, prolongationMethod: open.prolongationMethod,
        analysisSource: open.analysisSource,
        sources: { documents: picks, schedules: open.schedules || [] },
      };
      await api.patch(`/eot/claims/${open.id}`, payload);
      if (picks.length) {
        const { data } = await api.post(`/eot/claims/${open.id}/evidence/link`, { documents: picks });
        const added = (data.linked || []).length;
        toast.success(added
          ? `Setup saved — ${added} document(s) pulled into the claim`
          : 'Setup saved — the selected documents are already in the claim');
      } else {
        toast.success('Setup saved');
      }
      await refreshOpen(open.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const run = async (what) => {
    setBusy(what);
    try {
      await api.post(`/eot/claims/${open.id}/${what === 'rules' ? 'time-rules/extract' : 'events/detect'}`);
      const fresh = await refreshOpen(open.id);
      const state = what === 'rules' ? fresh.rules : fresh.events;
      // Only celebrate a read that is actually under way — an unreadable set
      // fails within the second.
      if (state?.status === 'RUNNING') {
        toast.success(what === 'rules' ? 'Cognara is reading the contract' : 'Cognara is reading the records');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start'); }
    finally { setBusy(''); }
  };

  const decideRule = async (ruleId, patch) => {
    try { await api.patch(`/eot/claims/${open.id}/time-rules/${ruleId}`, patch); await refreshOpen(open.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const decideEvent = async (eventId, patch) => {
    try { await api.patch(`/eot/claims/${open.id}/events/${eventId}`, patch); await refreshOpen(open.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const toggleLock = async () => {
    try { await api.post(`/eot/claims/${open.id}/time-rules/lock`, { locked: !rules?.locked }); await refreshOpen(open.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not lock'); }
  };

  const setupFields = (state, setState) => (
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Claim title"><Input value={state.title} onChange={e => setState({ ...state, title: e.target.value })}
        placeholder="EoT No. 1 — late access and drawing delays" data-testid="eot-title" /></Field>
      <Picker label="Claim type" options={(options?.claimTypes || []).map(k => ({ key: k, label: k.toLowerCase() }))}
              value={state.claimType} onChange={v => setState({ ...state, claimType: v })} testId="eot-claim-type" />
      <Picker label="Analysis source" testId="eot-analysis-source" value={state.analysisSource}
              onChange={v => setState({ ...state, analysisSource: v })}
              hint="Which already-completed reading of the papers the builder starts from."
              options={[{ key: 'COGNARA_AI', label: 'Cognara AI Analysis', tip: 'Uses the Cognara contract review of the selected documents — risks, clauses and claim opportunities.' },
                        { key: 'LD_ANALYSIS', label: 'LD Analysis', tip: 'Starts from the LD tracker position: rate, cap, delay days and the defences already recorded.' }]} />
      <Picker label="Claimant" value={state.claimant} onChange={v => setState({ ...state, claimant: v })} testId="eot-claimant"
              options={[{ key: 'CONTRACTOR', label: 'Contractor claim' }, { key: 'EMPLOYER', label: 'Employer-side assessment' }]} />
      <Field label="Claim period from"><Input type="date" value={state.periodFrom}
        onChange={e => setState({ ...state, periodFrom: e.target.value })} data-testid="eot-period-from" /></Field>
      <Field label="Claim period to"><Input type="date" value={state.periodTo}
        onChange={e => setState({ ...state, periodTo: e.target.value })} data-testid="eot-period-to" /></Field>
      <Field label="Contract start"><Input type="date" value={state.contractStartDate}
        onChange={e => setState({ ...state, contractStartDate: e.target.value })} data-testid="eot-contract-start" /></Field>
      <Field label="Original completion"><Input type="date" value={state.originalCompletionDate}
        onChange={e => setState({ ...state, originalCompletionDate: e.target.value })} data-testid="eot-original-completion" /></Field>
      <Field label="EoT already granted (days)"><Input type="number" value={state.eotAlreadyGrantedDays}
        onChange={e => setState({ ...state, eotAlreadyGrantedDays: e.target.value })} data-testid="eot-granted-days" /></Field>
      <div className="md:col-span-2 space-y-4">
        <Picker label="Delay analysis method" multi options={options?.analysisMethods || []} value={state.methods}
                onChange={v => setState({ ...state, methods: v })} testId="eot-methods"
                hint="Hover any method for what it does and when it is strongest. Pick more than one to compare." />
        <Picker label="Concurrency treatment" options={options?.concurrencyRules || []} value={state.concurrencyRule}
                onChange={v => setState({ ...state, concurrencyRule: v })} testId="eot-concurrency"
                hint="Used only where the contract is silent; a clause found in Time Rules overrides it." />
        <Picker label="Prolongation cost basis" options={options?.prolongationMethods || []} value={state.prolongationMethod}
                onChange={v => setState({ ...state, prolongationMethod: v })} testId="eot-prolongation" />
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
            Programmes — macro, micro and discipline ({(state.schedules || []).length} selected)
          </p>
          <div className="max-h-40 overflow-auto rounded border border-border p-2 space-y-1" data-testid="eot-schedules">
            {(sources.schedules || []).length === 0 && <p className="text-[11px] text-muted-foreground">No programmes on this project yet.</p>}
            {(sources.schedules || []).map(s => {
              const on = (state.schedules || []).some(x => x.id === s.id);
              return (
                <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`eot-schedule-${s.id}`}>
                  <input type="checkbox" checked={on} onChange={() => setState({
                    ...state, schedules: on ? state.schedules.filter(x => x.id !== s.id) : [...(state.schedules || []), s] })} />
                  <span className="text-foreground">{s.name}</span>
                  <Badge variant="outline" className="text-[9px]">{s.kind}</Badge>
                  {s.updates ? <span className="text-muted-foreground text-[10px]">{s.updates} update(s)</span> : null}
                  {s.activities ? <span className="text-muted-foreground text-[10px]">{s.activities} activities</span> : null}
                </label>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
            Project documents — contract, correspondence and records ({(state.documents || []).length} selected)
          </p>
          <div className="max-h-40 overflow-auto rounded border border-border p-2 space-y-1" data-testid="eot-documents">
            {(sources.documents || []).length === 0 && <p className="text-[11px] text-muted-foreground">No documents on this project yet — upload them in the claim instead.</p>}
            {(sources.documents || []).map(d => {
              const on = (state.documents || []).some(x => x.id === d.id);
              return (
                <label key={d.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`eot-document-${d.id}`}>
                  <input type="checkbox" checked={on} onChange={() => setState({
                    ...state, documents: on ? state.documents.filter(x => x.id !== d.id) : [...(state.documents || []), d] })} />
                  <span className="text-foreground truncate max-w-[340px]" title={d.name}>{d.name}</span>
                  {(d.analyses || []).map(a => <Badge key={a} variant="outline" className="text-[9px]">{a === 'LD_ANALYSIS' ? 'LD Analysis' : 'Cognara AI'}</Badge>)}
                  {(d.analyses || []).length === 0 && <Badge variant="outline" className="text-[9px] opacity-60">read on link</Badge>}
                </label>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Ticked documents are pulled into this claim's repository when you save, so Time Rules and Events can read them.
          </p>
        </div>
      </div>
    </div>
  );

  // ── list view ───────────────────────────────────────────────────────────
  if (!open) {
    if (startNew) {
      return (
        <Card className="bg-card border-border" data-testid="eot-new-workspace">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={onBack} data-testid="eot-new-back-btn">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <CardTitle className="text-foreground">New EoT claim</CardTitle>
                <CardDescription className="text-[11px]">
                  {contractLabel ? `${contractLabel} · ` : ''}raised in the claims register, then built here
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-wrap gap-1 text-[11px] text-muted-foreground border-b border-border pb-2">
              {['Setup', 'Time Rules', 'Events', 'Schedule Health', 'Analysis', 'Entitlement',
                'Claim Pack', 'Review & Tracking'].map((label, i) => (
                <span key={label} className={`px-2.5 py-1 rounded-md ${i === 0
                  ? 'bg-muted text-foreground font-medium' : 'opacity-50'}`}>{label}</span>
              ))}
            </div>
            {setupFields(form, setForm)}
            <Button onClick={createInRegister} disabled={!form.title.trim() || busy === 'create'}
                    data-testid="eot-create-btn">
              {busy === 'create' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create claim
            </Button>
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />The remaining tabs open once the claim exists. Dates are read
              from the documents you attach.
            </p>
          </CardContent>
        </Card>
      );
    }
    if (sourceClaim) {
      return (
        <Card className="bg-card border-border" data-testid="eot-workspace-loading">
          <CardContent className="py-10 text-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#6B5CE7] mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Opening the EoT workspace for {sourceClaim.claim_number || sourceClaim.title}…</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <>
        <Card className="bg-card border-border" data-testid="eot-claims-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-foreground flex items-center gap-2"><Gavel className="w-4 h-4 text-[#6B5CE7]" />EoT Claims</CardTitle>
                <CardDescription>Entitled extension of time computed from the programmes and the contract — claim-ready analysis for expert review.</CardDescription>
              </div>
              <Button onClick={() => setShowNew(true)} data-testid="eot-new-claim-btn"
                      className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]"><Plus className="w-4 h-4 mr-2" />New EoT Claim</Button>
            </div>
          </CardHeader>
          <CardContent>
            {claims.length === 0 ? (
              <div className="text-center py-10" data-testid="eot-empty">
                <CalendarClock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No EoT claims yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {claims.map(c => (
                  <div key={c.id} onClick={() => refreshOpen(c.id)} data-testid={`eot-claim-row-${c.id}`}
                       className="p-3 rounded-lg border border-border cursor-pointer hover:border-[#6B5CE7]/50 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-foreground font-medium truncate">{c.title}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {c.claimType} · {c.claimant === 'EMPLOYER' ? 'Employer assessment' : 'Contractor claim'}
                          {c.periodFrom ? ` · ${c.periodFrom} → ${c.periodTo || 'open'}` : ''}
                          {` · ${c.evidenceCount || 0} document(s) · ${c.eventCount || 0} confirmed event(s)`}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-[9px] shrink-0">{c.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="eot-new-dialog">
            <DialogHeader><DialogTitle>New EoT claim</DialogTitle></DialogHeader>
            {setupFields(form, setForm)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button onClick={create} disabled={!form.title.trim() || busy === 'create'} data-testid="eot-create-btn">
                {busy === 'create' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Create claim
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ── claim workspace ─────────────────────────────────────────────────────
  const evidence = open.evidence || [];
  return (
    <Card className="bg-card border-border" data-testid="eot-workspace">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button size="sm" variant="ghost" data-testid="eot-back-btn"
                    onClick={() => { if (onBack) { onBack(); } else { setOpen(null); loadClaims(); } }}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <CardTitle className="text-foreground truncate">{open.title}</CardTitle>
              <CardDescription className="text-[11px]">
                {open.claimNumber ? `${open.claimNumber} · ` : ''}
                {open.claimType} · {open.methods?.join(' + ')} · {open.concurrencyRule} · {open.prolongationMethod}
              </CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="text-[9px] shrink-0">{open.status}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            {[['setup', 'Setup'], ['rules', `Time Rules (${rules?.rules?.length || 0})`],
              ['events', `Events (${events?.events?.length || 0})`], ['health', 'Schedule Health'],
              ['analysis', 'Analysis'], ['entitlement', 'Entitlement'], ['pack', 'Claim Pack'],
              ['review', 'Review & Tracking'], ['guide', 'How this works']].map(([k, label]) => (
              <TabsTrigger key={k} value={k} data-testid={`eot-tab-${k}`}>{label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="setup" className="space-y-5 pt-4">
            {setupFields(open, (next) => setOpen(next))}
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={saveSetup} disabled={busy === 'save'} data-testid="eot-save-setup">
                {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Save setup
              </Button>
              {(open.documents || []).length > 0 && (
                <Button size="sm" variant="outline" onClick={() => linkDocs(open.documents)}
                        disabled={busy === 'link'} data-testid="eot-link-selected">
                  <Link2 className="w-3.5 h-3.5 mr-1.5" />Pull selected documents into the claim
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => readDates(true)} disabled={busy === 'dates'}
                      data-testid="eot-read-dates">
                {busy === 'dates' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <CalendarClock className="w-3.5 h-3.5 mr-1.5" />}
                Read the dates from the documents
              </Button>
            </div>
            {open.datesSource === 'COGNARA_DOCUMENTS' && (
              <p className="text-[10px] text-muted-foreground" data-testid="eot-dates-source">
                Dates above were read from the attached documents by Cognara AI — edit any of them if the
                papers say otherwise.
              </p>
            )}
            {(open.datesEvidence || []).length > 0 && (
              <div className="rounded border border-border p-2 space-y-1" data-testid="eot-dates-evidence">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">What the papers say about the dates</p>
                {open.datesEvidence.slice(0, 6).map((v, i) => (
                  <p key={i} className="text-[10px] text-muted-foreground">
                    <span className="text-foreground">{v.field}:</span> {v.value}
                    {v.clauseReference ? ` · Cl. ${v.clauseReference}` : ''}{v.page ? ` · p.${v.page}` : ''}
                    {v.quotedText ? ` — “${v.quotedText.slice(0, 160)}”` : ''}
                  </p>
                ))}
              </div>
            )}

            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-foreground font-medium">Evidence repository ({evidence.length})</p>
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" multiple className="hidden" data-testid="eot-file-input"
                         onChange={e => upload(e.target.files)} />
                  {evidence.length > 0 && (
                    <Button size="sm" variant="outline" onClick={rescan}
                            disabled={busy === 'rescan' || open.rescanStatus === 'RUNNING'}
                            data-testid="eot-rescan-btn">
                      {busy === 'rescan' || open.rescanStatus === 'RUNNING'
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                      Re-read with Cognara AI
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy === 'upload'}
                          data-testid="eot-upload-btn">
                    {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                    Upload documents
                  </Button>
                </div>
              </div>
              {open.rescanStatus === 'RUNNING' && (
                <p className="text-[11px] text-muted-foreground mb-2" data-testid="eot-rescan-running">
                  Cognara is reading the attached documents — scans are read page by page, so a long contract
                  takes a few minutes.
                </p>
              )}
              {open.rescanStatus === 'READY' && open.rescanResult && (
                <p className="text-[11px] text-muted-foreground mb-2" data-testid="eot-rescan-result">
                  {open.rescanResult.read} of {open.rescanResult.attached} document(s) read
                  {(open.rescanResult.unreadable || open.rescanResult.unreadableHeld)
                    ? `, ${open.rescanResult.unreadable || open.rescanResult.unreadableHeld} could not be read`
                    : ''}.
                </p>
              )}
              {evidence.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nothing yet — upload the contract, correspondence and records, or pull in documents already analysed above.</p>
              ) : (
                <div className="space-y-1" data-testid="eot-evidence-list">
                  {evidence.map(ev => (
                    <div key={ev.id} className="flex items-center gap-2 text-xs py-1" data-testid={`eot-evidence-${ev.id}`}>
                      <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground truncate max-w-[320px]" title={ev.name}>{ev.name}</span>
                      <Badge variant="outline" className="text-[9px]">{ev.classification}</Badge>
                      <span className="text-muted-foreground text-[10px]">
                        {ev.textChars ? `${Math.round(ev.textChars / 1000)}k characters read`
                          : (ev.readNote || 'no text layer')}
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 ml-auto"
                              onClick={async () => { await api.delete(`/eot/claims/${open.id}/evidence/${ev.id}`); refreshOpen(open.id); }}
                              data-testid={`eot-evidence-delete-${ev.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="rules" className="space-y-3 pt-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm text-foreground font-medium">Time Rules {rules?.version ? `· version ${rules.version}` : ''}</p>
                <p className="text-[11px] text-muted-foreground">
                  {rules?.contractForm ? `${rules.contractForm}. ` : ''}{rules?.precedenceNote || 'Accept, edit or reject each rule, then lock the set.'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => run('rules')} disabled={rules?.status === 'RUNNING' || rules?.locked}
                        data-testid="eot-extract-rules">
                  {rules?.status === 'RUNNING' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  {rules?.rules?.length ? 'Re-read the contract' : 'Read the contract'}
                </Button>
                {rules?.rules?.length > 0 && (
                  <Button size="sm" onClick={toggleLock} data-testid="eot-lock-rules">
                    {rules.locked ? <><Unlock className="w-3.5 h-3.5 mr-1.5" />Unlock</> : <><Lock className="w-3.5 h-3.5 mr-1.5" />Lock this version</>}
                  </Button>
                )}
              </div>
            </div>
            {rules?.error && (
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-xs text-red-500" data-testid="eot-rules-error">{rules.error}</p>
                {evidence.length > 0 && (
                  <Button size="sm" variant="outline" onClick={rescan}
                          disabled={busy === 'rescan' || open.rescanStatus === 'RUNNING'}
                          data-testid="eot-rules-rescan">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />Re-read the documents
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setTab('setup')} data-testid="eot-rules-link-docs">
                  <Link2 className="w-3.5 h-3.5 mr-1.5" />Link documents in Setup
                </Button>
              </div>
            )}
            {rules?.status === 'RUNNING' && <RunProgress progress={rules.progress} what="the contract" testid="eot-rules-progress" />}
            {rules?.rules?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="eot-rules-table">
                  <thead className="text-muted-foreground uppercase text-[10px]">
                    <tr>{['Category', 'Rule', 'Position', 'Clause · page', 'Verbatim', 'Conf.', 'Decision', ''].map(h =>
                      <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rules.rules.map(r => (
                      <tr key={r.id} className="border-t border-border align-top" data-testid={`eot-rule-${r.id}`}>
                        <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{r.category}</Badge></td>
                        <td className="px-2 py-1.5 text-foreground max-w-[180px]">{r.title}
                          {r.gap && <div className="text-[10px] text-amber-600 mt-0.5">Gap: {r.gapQuestion}</div>}</td>
                        <td className="px-2 py-1.5 text-foreground/80 max-w-[240px]">{r.editedValue || r.value}</td>
                        <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                          {r.clauseReference ? `Cl. ${r.clauseReference}` : '—'}{r.page ? ` · p.${r.page}` : ''}
                          {r.documentReference && <div className="truncate max-w-[130px]" title={r.documentReference}>{r.documentReference}</div>}
                        </td>
                        <td className="px-2 py-1.5 text-[10px] italic text-muted-foreground max-w-[200px]">“{r.verbatim}”</td>
                        <td className="px-2 py-1.5 text-center">{r.confidencePct}%</td>
                        <td className="px-2 py-1.5"><Pill value={r.status} testId={`eot-rule-status-${r.id}`} /></td>
                        <td className="px-2 py-1.5">
                          {!rules.locked && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-emerald-600"
                                      onClick={() => decideRule(r.id, { status: 'ACCEPTED' })} data-testid={`eot-rule-accept-${r.id}`}><Check className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-600"
                                      onClick={() => setEditRule({ ...r, editedValue: r.editedValue || r.value })} data-testid={`eot-rule-edit-${r.id}`}><Pencil className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600"
                                      onClick={() => decideRule(r.id, { status: 'REJECTED' })} data-testid={`eot-rule-reject-${r.id}`}><X className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="events" className="space-y-3 pt-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm text-foreground font-medium">Delay event register</p>
                <p className="text-[11px] text-muted-foreground">
                  Notice compliance is measured against the locked rules
                  {events?.noticePeriodDays ? ` (${events.noticePeriodDays} days)` : ' (notice period not accepted yet)'} — only confirmed events proceed.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => run('events')} disabled={events?.status === 'RUNNING'}
                      data-testid="eot-detect-events">
                {events?.status === 'RUNNING' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {events?.events?.length ? 'Re-read the records' : 'Find delay events'}
              </Button>
            </div>
            {events?.error && (
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-xs text-red-500" data-testid="eot-events-error">{events.error}</p>
                {evidence.length > 0 && (
                  <Button size="sm" variant="outline" onClick={rescan}
                          disabled={busy === 'rescan' || open.rescanStatus === 'RUNNING'}
                          data-testid="eot-events-rescan">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />Re-read the documents
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setTab('setup')} data-testid="eot-events-link-docs">
                  <Link2 className="w-3.5 h-3.5 mr-1.5" />Link documents in Setup
                </Button>
              </div>
            )}
            {events?.status === 'RUNNING' && <RunProgress progress={events.progress} what="the records" testid="eot-events-progress" />}
            {events?.events?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="eot-events-table">
                  <thead className="text-muted-foreground uppercase text-[10px]">
                    <tr>{['ID', 'Event', 'Party', 'Window', 'Days', 'Classification', 'Notice', 'Evidence', 'Conf.', 'Decision', ''].map(h =>
                      <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {events.events.map(e => (
                      <tr key={e.id} className="border-t border-border align-top" data-testid={`eot-event-${e.id}`}>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{e.eventId}</td>
                        <td className="px-2 py-1.5 text-foreground max-w-[220px]">{e.title}
                          {e.description && <div className="text-[10px] text-muted-foreground mt-0.5">{e.description}</div>}
                          {e.relatedTo && <div className="text-[10px] text-blue-600 mt-0.5">caused by {e.relatedTo}</div>}</td>
                        <td className="px-2 py-1.5"><Pill value={e.responsibleParty} /></td>
                        <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{e.startDate || '—'}{e.endDate ? ` → ${e.endDate}` : ''}</td>
                        <td className="px-2 py-1.5 text-center">{e.durationDays ?? '—'}</td>
                        <td className="px-2 py-1.5 text-[10px] text-foreground/80">{(e.classification || '—').replaceAll('_', ' ').toLowerCase()}</td>
                        <td className="px-2 py-1.5">
                          <Pill value={e.noticeCompliance} testId={`eot-event-notice-${e.id}`} />
                          <div className="text-[10px] text-muted-foreground mt-0.5">{e.noticeDate || 'no notice found'}</div>
                        </td>
                        <td className="px-2 py-1.5 text-[10px] text-muted-foreground max-w-[180px]">
                          {(e.evidence || []).slice(0, 2).map((v, i) => (
                            <div key={i} className="truncate" title={`${v.documentReference} ${v.page ? `p.${v.page}` : ''} — ${v.quotedText || ''}`}>
                              {v.documentReference}{v.page ? ` p.${v.page}` : ''}
                            </div>
                          ))}
                          {(e.evidence || []).length > 2 && <div>+{e.evidence.length - 2} more</div>}
                        </td>
                        <td className="px-2 py-1.5 text-center">{e.confidencePct}%</td>
                        <td className="px-2 py-1.5"><Pill value={e.status} testId={`eot-event-status-${e.id}`} /></td>
                        <td className="px-2 py-1.5">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-emerald-600"
                                    onClick={() => decideEvent(e.id, { status: 'CONFIRMED' })} data-testid={`eot-event-confirm-${e.id}`}><Check className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600"
                                    onClick={() => decideEvent(e.id, { status: 'REJECTED' })} data-testid={`eot-event-reject-${e.id}`}><X className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="health">
            <ScheduleHealthTab claimId={open.id} />
          </TabsContent>

          <TabsContent value="analysis">
            <DelayAnalysisTab claimId={open.id} methods={open.methods || []} onRan={() => refreshOpen(open.id)} />
          </TabsContent>

          {Object.entries({
            entitlement: <EntitlementTab claimId={open.id} claim={open} onSaved={() => refreshOpen(open.id)} />,
            pack: <ClaimPackTab claimId={open.id} onChanged={() => refreshOpen(open.id)} />,
            review: <ReviewTrackingTab claimId={open.id} onChanged={() => refreshOpen(open.id)} />,
            guide: <EoTGuideTab />,
          }).map(([k, node]) => (
            <TabsContent key={k} value={k}>{node}</TabsContent>
          ))}
        </Tabs>

        <p className="text-[10px] text-muted-foreground mt-4 flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" />Claim-ready analysis for expert review. Cognara extracts and suggests; every day and every figure is computed, not written.
        </p>
      </CardContent>

      <Dialog open={!!editRule} onOpenChange={() => setEditRule(null)}>
        <DialogContent data-testid="eot-rule-edit-dialog">
          <DialogHeader><DialogTitle>Edit rule — {editRule?.title}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-muted-foreground">Clause {editRule?.clauseReference || '—'} · “{editRule?.verbatim}”</p>
          <Textarea rows={4} value={editRule?.editedValue || ''} data-testid="eot-rule-edit-value"
                    onChange={e => setEditRule({ ...editRule, editedValue: e.target.value })} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRule(null)}>Cancel</Button>
            <Button onClick={async () => { await decideRule(editRule.id, { editedValue: editRule.editedValue }); setEditRule(null); }}
                    data-testid="eot-rule-edit-save">Save as edited</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
